// ============================================================
// breakdown.ts — "Where the £X goes" (pure; breakdown.test.ts). Splits the
// TOTAL DAMAGES into what's already paid out (CRU, interim payments,
// retention), each deduction, and the net to the client — the segments of
// the preview's horizontal bar. The segments ALWAYS add up to total damages:
// CRU is derived as the remainder (total − interims − retention −
// deductions − net), which is exactly the CRU the engine applied.
// Zero segments are dropped (the legend lists only what's there).
// A credit back to the client (interim bills) means the charges and the net overlap
// by that amount: barTotalPence is what the bar's widths add up to.
// ============================================================
import type { SettlementFigures } from '../../types/settlement.types'

export type SegmentKey =
  | 'CRU' | 'INTERIM' | 'RETENTION' | 'SUCCESS_FEE' | 'COUNSEL' | 'SHORTFALL' | 'ATE' | 'DISBURSEMENTS' | 'ADVERSE' | 'NET'

export interface Segment { key: SegmentKey; label: string; pence: number }

const LABEL: Record<SegmentKey, string> = {
  CRU: 'CRU', INTERIM: 'Interim payments', RETENTION: 'Retention', SUCCESS_FEE: 'Success fee', COUNSEL: 'Counsel’s fee',
  SHORTFALL: 'Costs not recovered', ATE: 'ATE premium', DISBURSEMENTS: 'Disbursements', ADVERSE: 'Adverse costs', NET: 'Net to client',
}

export const breakdownSegments = (f: SettlementFigures): { totalPence: number; barTotalPence: number; segments: Segment[] } => {
  const deductions = f.successFeePence + f.counselSuccessFeeChargedPence + f.shortfallPence + f.atePence + f.disbursementsPence + f.adverseCostsOffsetPence
  const credit = (f.costsCreditPence ?? 0) + (f.disbursementCreditPence ?? 0)
  const cru = Math.max(0, f.totalDamagesPence - f.interimPaymentsPence - f.retentionPence - deductions + credit - f.netToClientPence)
  const all: [SegmentKey, number][] = [
    ['CRU', cru], ['INTERIM', f.interimPaymentsPence], ['RETENTION', f.retentionPence],
    ['SUCCESS_FEE', f.successFeePence], ['COUNSEL', f.counselSuccessFeeChargedPence], ['SHORTFALL', f.shortfallPence],
    ['ATE', f.atePence], ['DISBURSEMENTS', f.disbursementsPence], ['ADVERSE', f.adverseCostsOffsetPence],
    ['NET', f.netToClientPence],
  ]
  return {
    totalPence: f.totalDamagesPence,
    barTotalPence: f.totalDamagesPence + credit,
    segments: all.filter(([, p]) => p > 0).map(([key, pence]) => ({ key, label: LABEL[key], pence })),
  }
}

// Bar width for a segment, as a % of the total (at least a hairline so a
// tiny but real amount stays visible; the legend gives the exact £).
export const segmentWidthPct = (pence: number, totalPence: number): number =>
  totalPence <= 0 ? 0 : Math.max(0.8, (pence / totalPence) * 100)
